import * as React from "react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { RequiredMark } from "./required-mark";

export interface UnifiedDatePickerProps {
  label?: string;
  value?: Date;
  onChange?: (date: Date | undefined) => void;
  disabled?: boolean;
  disabledDates?: (date: Date) => boolean;
  error?: string;
  className?: string;
  wrapperClassName?: string;
  dateFormat?: string;
  id?: string;
  required?: boolean;
}

const UnifiedDatePicker = React.forwardRef<HTMLButtonElement, UnifiedDatePickerProps>(
  (
    {
      label,
      value,
      onChange,
      disabled,
      disabledDates,
      error,
      className,
      wrapperClassName,
      dateFormat = "PPP",
      id,
      required = false,
    },
    ref,
  ) => {
    const [open, setOpen] = React.useState(false);

    const hasValue = !!value;

    const handleSelect = (date: Date | undefined) => {
      onChange?.(date);
      setOpen(false);
    };

    return (
      <div className={cn("flex flex-col gap-2", wrapperClassName)}>
        <Popover open={open} onOpenChange={disabled ? undefined : setOpen}>
          <PopoverTrigger asChild>
            <button
              ref={ref}
              id={id}
              type="button"
              disabled={disabled}
              className={cn(
                "relative flex h-14 w-full cursor-pointer items-center rounded-lg border bg-white px-4 text-left transition-all duration-200",
                {
                  "border-blue-600": open,
                  "border-red-500": error,
                  "border-gray-300": !open && !error,
                  "pointer-events-none bg-gray-50": disabled,
                  "hover:border-gray-400": !disabled && !open && !error,
                },
                className,
              )}
            >
              {label && (
                <Label
                  className={cn(
                    "pointer-events-none absolute text-sm text-gray-500 transition-all duration-200 ease-in-out",
                    {
                      "-translate-y-3 text-xs": hasValue || open,
                      "translate-y-0 text-sm": !hasValue && !open,
                    },
                  )}
                  htmlFor={id}
                >
                  {label}
                  {required && <RequiredMark />}
                </Label>
              )}

              <span
                className={cn(
                  "flex-1 truncate text-sm",
                  hasValue ? "translate-y-1.5 text-gray-900" : "text-transparent",
                )}
              >
                {hasValue ? format(value, dateFormat, { locale: fr }) : "placeholder"}
              </span>

              <CalendarIcon className="ml-2 size-4 shrink-0 text-gray-400" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={value}
              onSelect={handleSelect}
              disabled={disabledDates}
            />
          </PopoverContent>
        </Popover>

        {error && <p className="text-xs text-red-500">{error}</p>}
      </div>
    );
  },
);

UnifiedDatePicker.displayName = "UnifiedDatePicker";

export { UnifiedDatePicker };
